import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiMail, FiBriefcase, FiTarget, FiTrash2, FiMapPin, FiMessageSquare, FiUpload, FiFile, FiX, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { leadsAPI, ibgeAPI } from '../services/api';
import { ContatosList } from '../components/leads';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchIcon = styled.div`
  font-size: 48px;
  color: ${theme.colors.primary[500]};
  opacity: 0.8;
`;

const SearchButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  border: none;
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: ${theme.spacing[4]};
  align-items: flex-end;
  width: 100%;
`;

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  margin: ${theme.spacing[4]} 0;
  padding: ${theme.spacing[2]};
`;

const ResultCard = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary[500]}80;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  ${props => props.$selected && `
    background: ${theme.colors.primary[500]}05;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  `}
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[2]};
`;

const ResultName = styled.h4`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
`;

const ResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const ResultTag = styled.span`
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]}20;
  color: ${theme.colors.primary[500]};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
`;

// Estilos para importação
const ImportSection = styled.div`
  background: ${theme.colors.background.tertiary};
  border: 2px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[500]}10;
  }
`;

const ImportIcon = styled.div`
  font-size: 48px;
  color: ${theme.colors.primary[500]};
  opacity: 0.8;
`;

const ImportText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  text-align: center;
  margin: 0;
`;

const ImportButton = styled.label`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  border: none;
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
  }

  input {
    display: none;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  width: 100%;
  margin: ${theme.spacing[4]} 0;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${theme.colors.border.primary};
  }
`;

// Modal de importação
const ImportModalContent = styled(ModalContent)`
  max-width: 600px;
`;

const ImportModalTitle = styled.h3`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ImportProgress = styled.div`
  width: 100%;
  margin: ${theme.spacing[4]} 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const ProgressText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  text-align: center;
  margin: ${theme.spacing[2]} 0 0 0;
`;

const ImportResult = styled.div`
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$success ? theme.colors.success[500] + '20' : theme.colors.error[500] + '20'};
  border: 1px solid ${props => props.$success ? theme.colors.success[500] : theme.colors.error[500]};
  margin: ${theme.spacing[4]} 0;
`;

const ResultStats = styled.div`
  display: flex;
  gap: ${theme.spacing[6]};
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
`;

const ResultStat = styled.div`
  text-align: center;

  .number {
    font-size: ${theme.fontSizes['2xl']};
    font-weight: 700;
    color: ${props => props.$success ? theme.colors.success[500] : props.$error ? theme.colors.error[500] : theme.colors.text.primary};
  }

  .label {
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.text.secondary};
  }
`;

const ErrorsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
`;

const ErrorItem = styled.div`
  color: ${theme.colors.error[500]};
  padding: ${theme.spacing[1]} 0;
  border-bottom: 1px solid ${theme.colors.border.primary};

  &:last-child {
    border-bottom: none;
  }
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[4]};
`;

const FileName = styled.span`
  flex: 1;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
`;

const RemoveFileButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.muted};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.error[500]};
  }
`;

// Lista de segmentos disponíveis
/** Separa por | ou ; e retorna lista de telefones únicos (deduplica por dígitos), um por linha. */
const telefonesUnicos = (str) => {
  if (!str || typeof str !== 'string') return [];
  const list = str.split(/\s*[|;]\s*/).map((t) => t.trim()).filter(Boolean);
  const seen = new Set();
  return list.filter((t) => {
    const key = t.replace(/\D/g, '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SEGMENTOS_OPTIONS = [
  { value: 'Revenda Veículos', label: 'Revenda Veículos' },
  { value: 'Concessionária', label: 'Concessionária' },
  { value: 'Loja de Motos', label: 'Loja de Motos' },
  { value: 'Locadora', label: 'Locadora' },
  { value: 'Oficina Mecânica', label: 'Oficina Mecânica' },
  { value: 'Funilaria', label: 'Funilaria' },
  { value: 'Autopeças', label: 'Autopeças' },
  { value: 'Despachante', label: 'Despachante' },
  { value: 'Seguradora', label: 'Seguradora' },
  { value: 'Financeira', label: 'Financeira' },
  { value: 'Outro', label: 'Outro' },
];

const LeadForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    email: '',
    empresa: '',
    segmento: '',
    cidade: '',
    estado: '',
    endereco: '',
    origem_id: '',
    funil_id: '',
    etapa_atual_id: '',
    observacoes: ''
  });

  const [contatos, setContatos] = useState([{ nome: '', telefone: '' }]);

  const [funisList, setFunisList] = useState([]);
  const [etapasList, setEtapasList] = useState([]);
  const [origensList, setOrigensList] = useState([]);
  const [estadosList, setEstadosList] = useState([]);
  const [cidadesList, setCidadesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Estados para importação
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Estados para busca na internet
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({ cidade: '', estado: '', municipio_ibge: null });
  const [searchApiSource, setSearchApiSource] = useState('serpapi'); // 'serpapi' | 'minhareceita'
  const [searchResults, setSearchResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState([]);
  const [searchCidadesList, setSearchCidadesList] = useState([]);
  const [loadingSearchCidades, setLoadingSearchCidades] = useState(false);

  // Ref para ignorar respostas de requisições antigas (evita mostrar contatos de outro lead)
  const loadIdRef = useRef(null);
  const fileInputRef = useRef(null);

  // Carregar dados do lead se estiver editando
  useEffect(() => {
    if (isEditing) {
      carregarLead();
    } else {
      loadIdRef.current = null;
      // Resetar estado quando for um novo lead
      setFormData({
        email: '',
        empresa: '',
        segmento: '',
        cidade: '',
        estado: '',
        endereco: '',
        origem_id: '',
        funil_id: '',
        etapa_atual_id: '',
        observacoes: ''
      });
      setContatos([{ nome: '', telefone: '' }]);
    }
  }, [id]);

  // Carregar dados auxiliares
  useEffect(() => {
    carregarDadosAuxiliares();
    carregarEstados();
  }, []);

  // Carregar etapas quando o funil mudar
  useEffect(() => {
    if (formData.funil_id) {
      carregarEtapas(formData.funil_id);
    } else {
      setEtapasList([]);
    }
  }, [formData.funil_id]);

  // Carregar cidades quando o estado mudar
  useEffect(() => {
    if (formData.estado) {
      carregarCidades(formData.estado);
    } else {
      setCidadesList([]);
    }
  }, [formData.estado]);

  // Carregar municípios da pesquisa na internet quando o estado da pesquisa mudar (mesma regra de ClienteForm/IBGE)
  useEffect(() => {
    const carregarSearchCidades = async (siglaEstado) => {
      try {
        setLoadingSearchCidades(true);
        const resp = await ibgeAPI.listarMunicipios(siglaEstado);
        const ordenadas = (resp.data || []).slice().sort((a, b) => a.nome.localeCompare(b.nome));
        setSearchCidadesList(ordenadas);
      } catch (err) {
        console.error('Erro ao carregar municípios (pesquisa):', err);
        setSearchCidadesList([]);
      } finally {
        setLoadingSearchCidades(false);
      }
    };
    if (searchParams.estado) {
      carregarSearchCidades(searchParams.estado);
    } else {
      setSearchCidadesList([]);
    }
  }, [searchParams.estado]);

  const carregarLead = async () => {
    const idAtual = id;
    loadIdRef.current = idAtual;
    try {
      setLoadingData(true);
      setContatos([{ nome: '', telefone: '' }]);
      const response = await leadsAPI.buscarPorId(idAtual);

      const lead = response?.data?.data;
      const responseLeadId = lead ? String(lead.id) : null;
      const idsIguais = responseLeadId === String(idAtual);
      const aindaEhEsteLead = loadIdRef.current === idAtual;

      // Só aplicar se a resposta for do lead que estamos visualizando (evita race condition)
      if (!aindaEhEsteLead) return;
      if (!lead || !idsIguais) return;

      if (response.data.success) {
        setFormData({
          email: lead.email || '',
          empresa: lead.empresa || '',
          segmento: lead.segmento || '',
          cidade: lead.cidade || '',
          estado: lead.estado || '',
          endereco: lead.endereco || '',
          origem_id: lead.origem_id || '',
          funil_id: lead.funil_id || '',
          etapa_atual_id: lead.etapa_atual_id || '',
          observacoes: lead.observacoes || ''
        });

        // Filtro defensivo: só usar contatos que vieram na resposta (já filtrados pelo backend por lead_id)
        const contatosParaSetar =
          lead.contatos && lead.contatos.length > 0
            ? lead.contatos.map((c) => ({
                nome: c.nome || '',
                telefone: c.telefone || ''
              }))
            : [{ nome: '', telefone: '' }];

        setContatos(contatosParaSetar);
      } else {
        setError('Erro ao carregar dados do lead');
      }
    } catch (err) {
      if (loadIdRef.current !== idAtual) return;
      setError('Erro ao conectar com o servidor');
    } finally {
      if (loadIdRef.current === idAtual) {
        setLoadingData(false);
      }
    }
  };

  const carregarDadosAuxiliares = async () => {
    try {
      const [funisResponse, origensResponse] = await Promise.all([
        leadsAPI.listarFunis(),
        leadsAPI.listarOrigens()
      ]);

      if (funisResponse.data.success) {
        setFunisList(funisResponse.data.data);
      }
      if (origensResponse.data.success) {
        setOrigensList(origensResponse.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados auxiliares:', err);
    }
  };

  const carregarEstados = async () => {
    try {
      const response = await ibgeAPI.listarEstados();
      const estadosOrdenados = (response.data || []).slice().sort((a, b) => a.nome.localeCompare(b.nome));
      setEstadosList(estadosOrdenados);
    } catch (err) {
      console.error('Erro ao carregar estados:', err);
    }
  };

  const carregarCidades = async (siglaEstado) => {
    try {
      setLoadingCidades(true);
      const response = await ibgeAPI.listarMunicipios(siglaEstado);
      const cidadesOrdenadas = (response.data || []).slice().sort((a, b) => a.nome.localeCompare(b.nome));
      setCidadesList(cidadesOrdenadas);
    } catch (err) {
      console.error('Erro ao carregar cidades:', err);
    } finally {
      setLoadingCidades(false);
    }
  };

  const carregarEtapas = async (funilId) => {
    try {
      setLoadingEtapas(true);
      const response = await leadsAPI.listarEtapas(funilId);
      
      if (response.data.success) {
        setEtapasList(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
    } finally {
      setLoadingEtapas(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFunilChange = (value) => {
    setFormData(prev => ({
      ...prev,
      funil_id: value,
      etapa_atual_id: '' // Limpar etapa ao mudar funil
    }));
  };

  const handleEstadoChange = (value) => {
    setFormData(prev => ({
      ...prev,
      estado: value,
      cidade: '' // Limpar cidade ao mudar estado
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações básicas
      if (!formData.empresa && (!contatos || contatos.length === 0)) {
        throw new Error('Empresa ou pelo menos um contato é obrigatório');
      }

      // Validar contatos
      const contatosValidos = contatos.filter(c => c.nome || c.telefone);
      if (contatosValidos.length === 0) {
        throw new Error('Pelo menos um contato com nome ou telefone é obrigatório');
      }

      const dadosParaEnvio = {
        email: formData.email?.trim() || null,
        empresa: formData.empresa?.trim() || null,
        segmento: formData.segmento || null,
        cidade: formData.cidade?.trim() || null,
        estado: formData.estado || null,
        endereco: formData.endereco?.trim() || null,
        origem_id: formData.origem_id || null,
        funil_id: formData.funil_id || null,
        etapa_atual_id: formData.etapa_atual_id || null,
        observacoes: formData.observacoes?.trim() || null,
        contatos: contatosValidos.map(c => ({
          nome: c.nome?.trim() || null,
          telefone: c.telefone?.trim() || null
        }))
      };

      let response;
      if (isEditing) {
        response = await leadsAPI.atualizar(id, dadosParaEnvio);
      } else {
        response = await leadsAPI.criar(dadosParaEnvio);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Lead atualizado com sucesso!' : 'Lead cadastrado com sucesso!');
        setTimeout(() => {
          navigate('/leads');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar lead');
      }
    } catch (err) {
      console.error('Erro ao salvar lead:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/leads');
  };

  const handleExcluirLead = async () => {
    try {
      setDeleting(true);
      setError(null);

      await leadsAPI.deletar(id);
      
      navigate('/leads');
    } catch (err) {
      console.error('Erro ao excluir lead:', err);
      setError('Erro ao excluir lead');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleAbrirModalExclusao = () => {
    setShowDeleteModal(true);
  };

  const handleCancelarExclusao = () => {
    setShowDeleteModal(false);
  };

  // Funções de importação
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setShowImportModal(true);
      setImportResult(null);
    }
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setImportFile(null);
    setImportResult(null);
  };

  const handleCloseImportModal = () => {
    if (!importing) {
      setShowImportModal(false);
      setImportFile(null);
      setImportResult(null);
      setImportProgress(0);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      setImportProgress(10);
      setError(null);

      // Simular progresso durante o upload
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await leadsAPI.importar(importFile);

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.data.success) {
        setImportResult({
          success: true,
          data: response.data.data,
          message: response.data.message
        });
      } else {
        setImportResult({
          success: false,
          error: response.data.error || 'Erro ao importar arquivo'
        });
      }
    } catch (err) {
      console.error('Erro na importação:', err);
      setImportResult({
        success: false,
        error: err.response?.data?.error || err.message || 'Erro ao importar arquivo'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleGoToList = () => {
    navigate('/leads');
  };

  const handleSearchInternet = async () => {
    if (!searchParams.cidade || !searchParams.estado) {
      setError('Informe a cidade e o estado para realizar a pesquisa');
      return;
    }
    if (searchApiSource === 'minhareceita' && !searchParams.municipio_ibge) {
      setError('Para Minha Receita, selecione um município da lista');
      return;
    }

    try {
      setSearching(true);
      setError(null);
      setSearchResults([]);
      setSelectedResults([]);
      setShowSearchModal(true);

      const response = await leadsAPI.pesquisarInternet(searchParams.cidade, searchParams.estado, searchParams.municipio_ibge, searchApiSource);

      if (response.data.success) {
        setSearchResults(response.data.data);
      } else {
        setError(response.data.error || 'Erro ao pesquisar revendas');
      }
    } catch (err) {
      console.error('Erro na pesquisa internet:', err);
      setError('Erro ao conectar com o serviço de pesquisa externa');
    } finally {
      setSearching(false);
    }
  };

  const toggleResultSelection = (result) => {
    setSelectedResults(prev => {
      const isSelected = prev.some(r => r.empresa === result.empresa && r.endereco === result.endereco);
      if (isSelected) {
        return prev.filter(r => !(r.empresa === result.empresa && r.endereco === result.endereco));
      }
      return [...prev, result];
    });
  };

  const handleImportSelected = async () => {
    if (selectedResults.length === 0) return;

    try {
      setImporting(true);
      setImportProgress(0);
      setShowSearchModal(false);
      setShowImportModal(true);

      // Enviar lote para o backend (mesma regra da importação xlsx: origem, funil, etapa e segmento no servidor)
      const leads = selectedResults.map((result) => ({
        empresa: result.empresa || 'Sem nome',
        cidade: result.cidade || null,
        estado: result.estado || null,
        endereco: result.endereco ?? null,
        site: result.site || null,
        email: result.email || null,
        telefone: result.telefone || ''
      }));

      setImportProgress(30);
      const response = await leadsAPI.importarInternet(leads);
      setImportProgress(100);

      if (response.data.success && response.data.data) {
        setImportResult({
          success: true,
          data: response.data.data
        });
      } else {
        setImportResult({
          success: false,
          error: response.data.error || 'Erro ao importar'
        });
      }

    } catch (err) {
      console.error('Erro na importação selecionada:', err);
      setImportResult({
        success: false,
        error: err.response?.data?.error || err.message || 'Erro ao conectar com o servidor'
      });
    } finally {
      setImporting(false);
      setSelectedResults([]);
    }
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Lead' : 'Novo Lead'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Lead' : 'Novo Lead'}
          </Title>
        </Header>

        {/* Seção de importação - apenas para novos leads */}
        {!isEditing && (
          <>
            <ImportSection>
              <ImportIcon>
                <FiUpload />
              </ImportIcon>
              <ImportText>
                Importe leads de um arquivo Excel (.xlsx)<br />
                <small style={{ color: theme.colors.text.muted }}>
                  Colunas esperadas: Nome da revenda, Telefone 1, Telefone 2, Telefone 3, Cidade, Estado, Endereço, Site, Email
                </small>
              </ImportText>
              <ImportButton>
                <FiFile />
                Selecionar Arquivo
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                />
              </ImportButton>
            </ImportSection>

            <ImportSection style={{ borderStyle: 'solid', borderColor: theme.colors.primary[500] + '40' }}>
              <SearchIcon>
                <FiTarget style={{ color: theme.colors.primary[500] }} />
              </SearchIcon>
              <ImportText>
                Pesquisar revendas diretamente na internet<br />
                <small style={{ color: theme.colors.text.muted }}>
                  Informe cidade e estado para buscar lojas de veículos
                </small>
              </ImportText>
              <FormGroup style={{ marginBottom: theme.spacing[3] }}>
                <Label>Fonte da pesquisa</Label>
                <div style={{ display: 'flex', gap: theme.spacing[4], flexWrap: 'wrap', marginTop: theme.spacing[1] }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="searchApiSource"
                      value="serpapi"
                      checked={searchApiSource === 'serpapi'}
                      onChange={() => setSearchApiSource('serpapi')}
                    />
                    <span>SerpAPI (Google)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="searchApiSource"
                      value="minhareceita"
                      checked={searchApiSource === 'minhareceita'}
                      onChange={() => setSearchApiSource('minhareceita')}
                    />
                    <span>Minha Receita (Receita Federal)</span>
                  </label>
                </div>
                <small style={{ color: theme.colors.text.muted, display: 'block', marginTop: theme.spacing[1] }}>
                  {searchApiSource === 'serpapi' ? 'Busca via Google. Requer chave SerpAPI em Keys de APIs.' : 'Busca via dados da Receita Federal. Não requer chave. Minha Receita exige município selecionado.'}
                </small>
              </FormGroup>
              <SearchGrid>
                <FormGroup style={{ gap: '4px' }}>
                  <Label>Estado</Label>
                  <Select
                    value={searchParams.estado}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, estado: e.target.value, cidade: '', municipio_ibge: null }))}
                    style={{ padding: '8px 12px' }}
                  >
                    <option value="">UF</option>
                    {estadosList.map((estado) => (
                      <option key={estado.sigla} value={estado.sigla}>
                        {estado.sigla}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup style={{ gap: '4px' }}>
                  <Label>Município</Label>
                  <Select
                    value={searchParams.cidade}
                    onChange={(e) => {
                      const nome = e.target.value;
                      const selected = searchCidadesList.find((c) => c.nome === nome);
                      setSearchParams(prev => ({ ...prev, cidade: nome, municipio_ibge: selected ? selected.id : null }));
                    }}
                    style={{ padding: '8px 12px' }}
                    disabled={!searchParams.estado || loadingSearchCidades}
                  >
                    <option value="">
                      {loadingSearchCidades ? 'Carregando...' : 'Selecione o município'}
                    </option>
                    {searchCidadesList.map((c) => (
                      <option key={c.id} value={c.nome}>{c.nome}</option>
                    ))}
                  </Select>
                </FormGroup>
                <SearchButton 
                  onClick={handleSearchInternet} 
                  disabled={!searchParams.cidade || !searchParams.estado || searching || (searchApiSource === 'minhareceita' && !searchParams.municipio_ibge)}
                >
                  <FiTarget />
                  {searching ? 'Pesquisando...' : 'Pesquisar'}
                </SearchButton>
              </SearchGrid>
            </ImportSection>

            <OrDivider>ou cadastre manualmente</OrDivider>
          </>
        )}

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiBriefcase />
              Informações da Empresa
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="empresa">Empresa</Label>
                <Input
                  id="empresa"
                  type="text"
                  placeholder="Nome da empresa"
                  value={formData.empresa}
                  onChange={(e) => handleInputChange('empresa', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">
                  <FiMail style={{ marginRight: '4px' }} />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="segmento">
                  <FiTarget style={{ marginRight: '4px' }} />
                  Segmento
                </Label>
                <Select
                  id="segmento"
                  value={formData.segmento}
                  onChange={(e) => handleInputChange('segmento', e.target.value)}
                >
                  <option value="">Selecione o segmento</option>
                  {SEGMENTOS_OPTIONS.map((segmento) => (
                    <option key={segmento.value} value={segmento.value}>
                      {segmento.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="origem">
                  <FiMapPin style={{ marginRight: '4px' }} />
                  Origem
                </Label>
                <Select
                  id="origem"
                  value={formData.origem_id}
                  onChange={(e) => handleInputChange('origem_id', e.target.value)}
                >
                  <option value="">Selecione a origem</option>
                  {origensList.map((origem) => (
                    <option key={origem.id} value={origem.id}>
                      {origem.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <ContatosList contatos={contatos} onChange={setContatos} />
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiMapPin />
              Localização
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="estado">Estado</Label>
                <Select
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                  disabled={isEditing}
                >
                  <option value="">Selecione o estado</option>
                  {estadosList.map((estado) => (
                    <option key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="cidade">Cidade</Label>
                {isEditing ? (
                  <Input
                    id="cidade"
                    type="text"
                    value={formData.cidade || ''}
                    disabled
                  />
                ) : (
                  <Select
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                    disabled={!formData.estado || loadingCidades}
                  >
                    <option value="">
                      {loadingCidades ? 'Carregando...' : 'Selecione a cidade'}
                    </option>
                    {cidadesList.map((cidade) => (
                      <option key={cidade.id} value={cidade.nome}>
                        {cidade.nome}
                      </option>
                    ))}
                  </Select>
                )}
              </FormGroup>

              <FormGroupFull>
                <Label htmlFor="endereco">Endereço</Label>
                <Input
                  id="endereco"
                  type="text"
                  placeholder="Rua, número, bairro..."
                  value={formData.endereco}
                  onChange={(e) => handleInputChange('endereco', e.target.value)}
                  disabled={isEditing}
                />
              </FormGroupFull>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiTarget />
              Prospecção
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="funil">Funil</Label>
                <Select
                  id="funil"
                  value={formData.funil_id}
                  onChange={(e) => handleFunilChange(e.target.value)}
                >
                  <option value="">Selecione o funil</option>
                  {funisList.map((funil) => (
                    <option key={funil.id} value={funil.id}>
                      {funil.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="etapa">Etapa</Label>
                <Select
                  id="etapa"
                  value={formData.etapa_atual_id}
                  onChange={(e) => handleInputChange('etapa_atual_id', e.target.value)}
                  disabled={!formData.funil_id || loadingEtapas}
                >
                  <option value="">
                    {loadingEtapas ? 'Carregando...' : 'Selecione a etapa'}
                  </option>
                  {etapasList.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.ordem}. {etapa.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiMessageSquare />
              Observações
            </SectionTitle>
            
            <FormGroupFull>
              <TextArea
                id="observacoes"
                placeholder="Informações adicionais sobre o lead..."
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
              />
            </FormGroupFull>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={handleAbrirModalExclusao} $danger>
                <ButtonIcon>
                  <FiTrash2 />
                </ButtonIcon>
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Lead</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O lead <strong>{formData.empresa || 'Sem empresa'}</strong>
              <br />
              • Todos os contatos relacionados
              <br />
              • Todas as interações relacionadas
              <br />
              • Todos os registros de prospecção
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluirLead} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de importação */}
      {showImportModal && (
        <ModalOverlay>
          <ImportModalContent>
            <ImportModalTitle>
              <FiUpload />
              Importar Leads
            </ImportModalTitle>

            {importFile && !importResult && (
              <SelectedFile>
                <FiFile style={{ color: theme.colors.primary[500] }} />
                <FileName>{importFile.name}</FileName>
                {!importing && (
                  <RemoveFileButton onClick={handleRemoveFile}>
                    <FiX />
                  </RemoveFileButton>
                )}
              </SelectedFile>
            )}

            {importing && (
              <ImportProgress>
                <ProgressBar>
                  <ProgressFill $progress={importProgress} />
                </ProgressBar>
                <ProgressText>Importando leads... {importProgress}%</ProgressText>
              </ImportProgress>
            )}

            {importResult && (
              <>
                <ImportResult $success={importResult.success}>
                  {importResult.success ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], marginBottom: theme.spacing[3] }}>
                        <FiCheckCircle style={{ color: theme.colors.success[500], fontSize: '24px' }} />
                        <span style={{ color: theme.colors.success[500], fontWeight: 600 }}>
                          Importação concluída!
                        </span>
                      </div>
                      <ResultStats>
                        <ResultStat>
                          <div className="number">{importResult.data.total}</div>
                          <div className="label">Total</div>
                        </ResultStat>
                        <ResultStat $success>
                          <div className="number">{importResult.data.importados}</div>
                          <div className="label">Importados</div>
                        </ResultStat>
                        {importResult.data.erros?.length > 0 && (
                          <ResultStat $error>
                            <div className="number">{importResult.data.erros.length}</div>
                            <div className="label">Erros</div>
                          </ResultStat>
                        )}
                      </ResultStats>
                      {importResult.data.erros?.length > 0 && (
                        <ErrorsList>
                          {importResult.data.erros.map((err, idx) => (
                            <ErrorItem key={idx}>
                              Linha {err.linha}: {err.erro}
                            </ErrorItem>
                          ))}
                        </ErrorsList>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                      <FiAlertCircle style={{ color: theme.colors.error[500], fontSize: '24px' }} />
                      <span style={{ color: theme.colors.error[500] }}>
                        {importResult.error}
                      </span>
                    </div>
                  )}
                </ImportResult>
              </>
            )}

            <ModalActions>
              {!importResult ? (
                <>
                  <ModalButton onClick={handleCloseImportModal} disabled={importing}>
                    Cancelar
                  </ModalButton>
                  <Button $primary onClick={handleImport} disabled={importing || !importFile}>
                    <ButtonIcon>
                      {importing ? <FiLoader /> : <FiUpload />}
                    </ButtonIcon>
                    {importing ? 'Importando...' : 'Importar'}
                  </Button>
                </>
              ) : (
                <>
                  <ModalButton onClick={handleCloseImportModal}>
                    Fechar
                  </ModalButton>
                  {importResult.success && importResult.data.importados > 0 && (
                    <Button $primary onClick={handleGoToList}>
                      <ButtonIcon>
                        <FiCheckCircle />
                      </ButtonIcon>
                      Ver Lista de Leads
                    </Button>
                  )}
                </>
              )}
            </ModalActions>
          </ImportModalContent>
        </ModalOverlay>
      )}

      {/* Modal de pesquisa na internet */}
      {showSearchModal && (
        <ModalOverlay>
          <ImportModalContent>
            <ImportModalTitle style={{ color: theme.colors.primary[500] }}>
              <FiTarget />
              Pesquisa na Internet: {searchParams.cidade} - {searchParams.estado}
            </ImportModalTitle>

            {searching ? (
              <ImportProgress>
                <ProgressBar>
                  <ProgressFill $progress={50} style={{ background: theme.colors.primary[500] }} />
                </ProgressBar>
                <ProgressText>Buscando revendas na internet... Aguarde.</ProgressText>
              </ImportProgress>
            ) : (
              <>
                <ImportText style={{ textAlign: 'left', marginBottom: theme.spacing[2] }}>
                  Encontramos {searchResults.length} estabelecimentos. Selecione os que deseja importar:
                </ImportText>
                {searchResults.length > 0 && (
                  <div style={{ display: 'flex', gap: theme.spacing[3], marginBottom: theme.spacing[3], flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedResults([...searchResults])}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: theme.colors.primary[500],
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '0.875rem',
                        textDecoration: 'underline'
                      }}
                    >
                      Selecionar todos
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedResults([])}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: theme.colors.text.muted,
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '0.875rem',
                        textDecoration: 'underline'
                      }}
                    >
                      Desmarcar todos
                    </button>
                  </div>
                )}

                <ResultsList>
                  {searchResults.map((res, idx) => {
                    const isSelected = selectedResults.some(r => r.empresa === res.empresa && r.endereco === res.endereco);
                    return (
                      <ResultCard 
                        key={idx} 
                        $selected={isSelected}
                        onClick={() => toggleResultSelection(res)}
                      >
                        <ResultHeader>
                          <ResultName>{res.empresa}</ResultName>
                          {isSelected && <ResultTag>Selecionado</ResultTag>}
                        </ResultHeader>
                        <ResultInfo>
                          {res.endereco && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiMapPin size={12} /> {res.endereco}
                            </div>
                          )}
                          {res.telefone && telefonesUnicos(res.telefone).map((tel, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiMessageSquare size={12} /> {tel}
                            </div>
                          ))}
                          {res.site && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiUpload size={12} /> {res.site}
                            </div>
                          )}
                          {res.email && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiMail size={12} /> {res.email}
                            </div>
                          )}
                        </ResultInfo>
                      </ResultCard>
                    );
                  })}
                  {searchResults.length === 0 && !searching && (
                    <div style={{ textAlign: 'center', padding: theme.spacing[8], color: theme.colors.text.muted }}>
                      Nenhuma revenda encontrada nesta localização.
                    </div>
                  )}
                </ResultsList>

                <ModalActions>
                  <ModalButton onClick={() => setShowSearchModal(false)}>
                    Cancelar
                  </ModalButton>
                  <Button 
                    $primary 
                    style={{ background: theme.colors.primary[500], borderColor: theme.colors.primary[500] }}
                    disabled={selectedResults.length === 0}
                    onClick={handleImportSelected}
                  >
                    <FiCheckCircle />
                    Importar Selecionados ({selectedResults.length})
                  </Button>
                </ModalActions>
              </>
            )}
          </ImportModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default LeadForm;
